import JSZip from 'jszip';
import { resolveDiscoveryIconsBucketKey } from '../../services/admin/discoveryIconsApi';
import { buildExportPayload } from './adminDraftPayload';
import {
  BUCKET_BOTS_BASE,
  buildBotIconExportUrl,
  buildDiscoveryIconExportUrl,
} from './botBucketImageUrls';
import { loadLocalBotIcon } from './botIconLocalStorage';
import { loadLocalDiscoveryIcons } from './discoveryIconsLocalStorage';

function isDataUrl(url) {
  return typeof url === 'string' && url.startsWith('data:');
}

function dataUrlToUint8Array(dataUrl) {
  if (!isDataUrl(dataUrl)) {
    throw new Error('invalid data url');
  }

  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex === -1) {
    throw new Error('invalid data url');
  }

  const meta = dataUrl.slice(0, commaIndex);
  const data = dataUrl.slice(commaIndex + 1);

  if (/;base64/i.test(meta)) {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  const decoded = decodeURIComponent(data);
  const bytes = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
}

export function exportUrlToZipPath(exportUrl) {
  if (typeof exportUrl !== 'string' || !exportUrl.startsWith(BUCKET_BOTS_BASE)) {
    return null;
  }

  const relative = exportUrl.slice(BUCKET_BOTS_BASE.length).replace(/^\/+/, '');
  if (!relative) {
    return null;
  }

  return `assets/bots/${relative}`;
}

function uniqueDataUrlsFromDiscoveryButtons(buttons) {
  if (!Array.isArray(buttons)) {
    return [];
  }

  const seen = new Set();
  const out = [];

  buttons.forEach((btn) => {
    const url = btn?.icon;
    if (!isDataUrl(url) || seen.has(url)) {
      return;
    }
    seen.add(url);
    out.push(url);
  });

  return out;
}

export function collectExportAssets({ domainName, botName, draft }) {
  const assets = [];
  const bucketKey = resolveDiscoveryIconsBucketKey(botName, draft);

  if (isDataUrl(draft?.botIconUrl)) {
    const local = loadLocalBotIcon(domainName, botName);
    const dataUrl = local?.url || draft.botIconUrl;
    const fileName = local?.fileName || 'icon';
    const mimeType = local?.mimeType || 'image/svg+xml';
    const exportUrl = buildBotIconExportUrl(bucketKey, fileName, mimeType);
    const zipPath = exportUrlToZipPath(exportUrl);

    if (zipPath) {
      assets.push({
        zipPath,
        bytes: dataUrlToUint8Array(dataUrl),
      });
    }
  }

  const localIcons = loadLocalDiscoveryIcons(bucketKey);
  const iconByUrl = new Map(localIcons.map((icon) => [icon.url, icon]));
  const usedDataUrls = uniqueDataUrlsFromDiscoveryButtons(draft?.discoveryButton);

  usedDataUrls.forEach((dataUrl) => {
    const meta = iconByUrl.get(dataUrl);
    const fileName = meta?.fileName || meta?.label || 'icon';
    const exportUrl = buildDiscoveryIconExportUrl(fileName);
    const zipPath = exportUrlToZipPath(exportUrl);

    if (zipPath) {
      assets.push({
        zipPath,
        bytes: dataUrlToUint8Array(dataUrl),
      });
    }
  });

  return assets;
}

export async function buildAdminExportZip({ botName, domainName, draft }) {
  const payload = buildExportPayload(draft, { botName, domainName });
  const assets = collectExportAssets({ domainName, botName, draft });
  const zip = new JSZip();

  zip.file(`${botName}_config.json`, JSON.stringify(payload, null, 2));
  assets.forEach(({ zipPath, bytes }) => {
    zip.file(zipPath, bytes);
  });

  return zip.generateAsync({ type: 'blob' });
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadAdminExportZip({ botName, domainName, draft }) {
  const blob = await buildAdminExportZip({ botName, domainName, draft });
  downloadBlob(`${botName}_export.zip`, blob);
}
